import { z } from 'zod';
import { moneySchema } from './common.schemas';
import { defineListQuery, inForceFilter, listFilter } from './list-query.schemas';

/** Rate cards and rate lines (PRD → Rate cards; gitaumoses4/chukua#23): a lookup with an effective date, not a static price list. */

const codeSchema = z.string().trim().min(1).max(32);
const nameSchema = z.string().trim().min(1).max(120);

/** Required on every rate-line and proposal mutation (gitaumoses4/chukua#24) — the audit trail's "why", not just "what". */
export const reasonSchema = z.string().trim().min(1).max(500);

/** Cargo vocabularies a rate line matches by code — a deployment's own lists, like zone kinds. */
export const containerSizeInputSchema = z.object({ code: codeSchema, name: nameSchema });
export type ContainerSizeInput = z.infer<typeof containerSizeInputSchema>;

export const containerTypeInputSchema = z.object({
  code: codeSchema,
  name: nameSchema,
  /** A reefer: the truck needs a genset or the trailer must be a reefer (gitaumoses4/chukua#48). */
  needsReeferPower: z.boolean().default(false),
  /** A flat rack: the trailer must be skeletal or a flat rack (gitaumoses4/chukua#48). */
  needsSkeletalOrFlatRack: z.boolean().default(false),
});
export type ContainerTypeInput = z.input<typeof containerTypeInputSchema>;

/** `maxKg` null is open-ended ("over 10 t"). */
export const weightBandInputSchema = z
  .object({
    code: codeSchema,
    name: nameSchema,
    minKg: z.number().int().min(0),
    maxKg: z.number().int().nullable().default(null),
  })
  .refine((band) => band.maxKg === null || band.maxKg > band.minKg, { message: 'maxKg must be greater than minKg', path: ['maxKg'] });
export type WeightBandInput = z.infer<typeof weightBandInputSchema>;

export const RATE_BASES = ['per_trip', 'per_container', 'per_tonne', 'per_km'] as const;
export type RateBasis = (typeof RATE_BASES)[number];

/** What a line is priced against: a container (size + type) or a weight band, never both. */
const cargoKeyShape = {
  containerSizeCode: codeSchema.optional(),
  containerTypeCode: codeSchema.optional(),
  weightBandCode: codeSchema.optional(),
};

interface CargoKey {
  containerSizeCode?: string;
  containerTypeCode?: string;
  weightBandCode?: string;
}

function isContainerCargo(key: CargoKey): boolean {
  return key.containerSizeCode !== undefined && key.containerTypeCode !== undefined && key.weightBandCode === undefined;
}

function isLooseCargo(key: CargoKey): boolean {
  return key.containerSizeCode === undefined && key.containerTypeCode === undefined && key.weightBandCode !== undefined;
}

const CARGO_MESSAGE = 'Give a container size and type, or a weight band, not both and not neither';

/** Ends up as `rate_lines_cargo_check` in the database; here so a form gets the message before a round trip. */
export const rateLineInputSchema = z
  .object({
    originZoneCode: codeSchema,
    destinationZoneCode: codeSchema,
    jobTypeCode: codeSchema,
    ...cargoKeyShape,
    basis: z.enum(RATE_BASES),
    amount: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Amount cannot be negative', path: ['amountMinor'] }),
    /** Required on container lines (a recurring billing dispute, so never a silent default); not applicable to loose cargo. */
    emptyReturnIncluded: z.boolean().optional(),
    validFrom: z.iso.date(),
    validTo: z.iso.date().optional(),
    reason: reasonSchema,
  })
  .refine((line) => isContainerCargo(line) || isLooseCargo(line), { message: CARGO_MESSAGE, path: ['containerSizeCode'] })
  .refine((line) => !isContainerCargo(line) || line.emptyReturnIncluded !== undefined, {
    message: 'Say whether the empty return is included or billed separately',
    path: ['emptyReturnIncluded'],
  })
  .refine((line) => !isLooseCargo(line) || line.emptyReturnIncluded === undefined, {
    message: 'Empty return applies to container lines only',
    path: ['emptyReturnIncluded'],
  })
  .refine((line) => line.validTo === undefined || line.validTo >= line.validFrom, { message: 'validTo cannot be before validFrom', path: ['validTo'] });
export type RateLineInput = z.infer<typeof rateLineInputSchema>;

/** `POST /rate-lines/:id/supersede` — the key and basis carry over; the flag does too unless restated. */
export const supersedeRateLineInputSchema = z.object({
  amount: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Amount cannot be negative', path: ['amountMinor'] }),
  emptyReturnIncluded: z.boolean().optional(),
  effectiveDate: z.iso.date(),
  reason: reasonSchema,
});
export type SupersedeRateLineInput = z.infer<typeof supersedeRateLineInputSchema>;

/** `POST /rate-lines/:id/end` — last day the line applies, inclusive. */
export const endRateLineInputSchema = z.object({ validTo: z.iso.date(), reason: reasonSchema });
export type EndRateLineInput = z.infer<typeof endRateLineInputSchema>;

/** `PATCH /rate-line-proposals/:id` — a draft's author adjusts price, flag or dates before submitting; the key and kind never change. */
export const rateLineProposalEditSchema = z.object({
  amount: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Amount cannot be negative', path: ['amountMinor'] }).optional(),
  emptyReturnIncluded: z.boolean().optional(),
  validFrom: z.iso.date().optional(),
  validTo: z.iso.date().optional(),
  reason: reasonSchema,
});
export type RateLineProposalEditInput = z.infer<typeof rateLineProposalEditSchema>;

/** `POST /rate-line-proposals/:id/{submit,withdraw,approve,reject}` — every step needs the reason for its own audit row. */
export const rateLineProposalDecisionSchema = z.object({ reason: reasonSchema });
export type RateLineProposalDecisionInput = z.infer<typeof rateLineProposalDecisionSchema>;

/** No separate "rejected" status: a reject returns a proposal straight to `draft` (reason kept on `lastDecisionReason`) so the author can revise and resubmit it. `approved` and `withdrawn` are terminal. */
export const RATE_LINE_PROPOSAL_STATUSES = ['draft', 'pending_approval', 'approved', 'withdrawn'] as const;
export type RateLineProposalStatus = (typeof RATE_LINE_PROPOSAL_STATUSES)[number];

export const RATE_LINE_PROPOSAL_KINDS = ['create', 'supersede'] as const;
export type RateLineProposalKind = (typeof RATE_LINE_PROPOSAL_KINDS)[number];

export const rateLineProposalListQuerySchema = defineListQuery({
  sortFields: ['createdAt', 'validFrom'],
  defaultSort: { sort: 'createdAt', order: 'desc' },
  filters: {
    status: listFilter(z.enum(RATE_LINE_PROPOSAL_STATUSES)),
    kind: listFilter(z.enum(RATE_LINE_PROPOSAL_KINDS)),
    /** The proposals that produced these lines: one call gives a whole line's history its approvers. */
    rateLineId: listFilter(z.string().uuid()),
  },
});
export type RateLineProposalListQuery = z.infer<typeof rateLineProposalListQuerySchema>;

/** `POST /rate-cards` — no `customerId` creates the standard card. */
export const createRateCardInputSchema = z.object({ customerId: z.string().uuid().nullable().default(null) });
export type CreateRateCardInput = z.infer<typeof createRateCardInputSchema>;

export const rateCardListQuerySchema = defineListQuery({
  sortFields: ['createdAt'],
  defaultSort: { sort: 'createdAt', order: 'desc' },
  filters: { customerId: z.string().uuid().optional(), status: listFilter(z.enum(['active', 'inactive'])) },
});
export type RateCardListQuery = z.infer<typeof rateCardListQuerySchema>;

export const rateLineListQuerySchema = defineListQuery({
  sortFields: ['validFrom', 'amountMinor', 'createdAt'],
  defaultSort: { sort: 'validFrom', order: 'desc' },
  filters: {
    asOf: z.iso.date().optional(),
    current: inForceFilter,
    originZoneCode: listFilter(codeSchema),
    destinationZoneCode: listFilter(codeSchema),
    jobTypeCode: listFilter(codeSchema),
    basis: listFilter(z.enum(RATE_BASES)),
  },
});
export type RateLineListQuery = z.infer<typeof rateLineListQuerySchema>;

/**
 * `GET /rate-cards/:id/lines/export` — history included only when asked, same idiom as `inForceFilter`
 * (never `z.coerce.boolean()`, which reads the query string "false" as true).
 */
export const rateLineExportQuerySchema = z.object({
  includeSuperseded: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
});
export type RateLineExportQuery = z.infer<typeof rateLineExportQuerySchema>;

/** `POST /rate-cards/:id/lines/import/commit` — the one reason recorded on every proposal the import creates. */
export const rateLineImportCommitSchema = z.object({ reason: reasonSchema });
export type RateLineImportCommitInput = z.infer<typeof rateLineImportCommitSchema>;

/** `GET /rate-lookup` — the key plus the job's date. No `customerId` looks on the standard card only. */
export const rateLookupQuerySchema = z
  .object({
    customerId: z.string().uuid().optional(),
    originZoneCode: codeSchema,
    destinationZoneCode: codeSchema,
    jobTypeCode: codeSchema,
    ...cargoKeyShape,
    basis: z.enum(RATE_BASES),
    jobDate: z.iso.date(),
  })
  .refine((key) => isContainerCargo(key) || isLooseCargo(key), { message: CARGO_MESSAGE, path: ['containerSizeCode'] });
export type RateLookupQuery = z.infer<typeof rateLookupQuerySchema>;
